import * as React from "react"
import { ArrowRight, Brain, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { formatAmount } from "@/lib/format"
import { useI18n, t } from "@/lib/i18n"
import type { ReviewItem } from "@/lib/types"
import { cn } from "@/lib/utils"

import {
  ACCOUNT_OPTIONS,
  COST_CENTER_OPTIONS,
  TAX_TREATMENT_OPTIONS,
  type CorrectionPayload,
} from "@/routes/review/api"

const MIN_REASON_LENGTH = 10

interface CorrectionDialogProps {
  item: ReviewItem | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * El plan de cuentas REAL (`GET /ledger/accounts`).
   *
   * `CorreccionIn.cuenta_local` es el CODIGO contra `account_local`, y el
   * catalogo sembrado llega a la divisionaria de 3 digitos ("639"). Las cuentas
   * fijas de este dialogo eran subdivisionarias de 5 ("63911") que no existen:
   * toda correccion contra el backend real moria en 422. Sin catalogo cargado se
   * cae a la lista fija, que es la correcta en modo mock.
   */
  accounts?: { code: string; name: string }[]
  /**
   * Los centros de costo REALES. El backend los identifica por UUID; la pantalla
   * trabaja con codigos y el mapeo resuelve codigo -> uuid. Si el maestro esta
   * vacio, el centro de costo simplemente no viaja (ver `correctionToApi`).
   */
  costCenters?: { code: string; name: string }[]
  pending?: boolean
  onSubmit: (payload: CorrectionPayload) => void | Promise<void>
}

/**
 * Correccion de una clasificacion.
 *
 * Lo importante no son los tres selects: es el DIFF. Quien corrige tiene que
 * ver de un vistazo que valor cambia y a que, porque eso es exactamente lo que
 * va a quedar escrito en el audit log con su nombre. Por eso el antes/despues
 * se muestra en columnas alineadas y solo se resalta lo que efectivamente
 * cambio.
 */
export function CorrectionDialog({
  item,
  open,
  onOpenChange,
  accounts: accountsProp,
  costCenters: costCentersProp,
  pending,
  onSubmit,
}: CorrectionDialogProps) {
  const { term } = useI18n()

  const accounts =
    accountsProp && accountsProp.length > 0 ? accountsProp : ACCOUNT_OPTIONS
  const costCenters =
    costCentersProp && costCentersProp.length > 0 ? costCentersProp : COST_CENTER_OPTIONS
  const [accountCode, setAccountCode] = React.useState("")
  const [costCenter, setCostCenter] = React.useState("")
  const [taxTreatment, setTaxTreatment] = React.useState("")
  const [reason, setReason] = React.useState("")
  const [touched, setTouched] = React.useState(false)

  React.useEffect(() => {
    if (open && item) {
      setAccountCode(item.accountCode)
      setCostCenter(item.costCenter ?? "")
      setTaxTreatment(item.taxTreatment)
      setReason("")
      setTouched(false)
    }
  }, [open, item])

  if (!item) return null

  const trimmed = reason.trim()
  const reasonInvalid = trimmed.length < MIN_REASON_LENGTH
  const accountName =
    accounts.find((option) => option.code === accountCode)?.name ?? item.accountName

  const changes = [
    {
      label: "Cuenta contable",
      before: `${item.accountCode} · ${item.accountName}`,
      after: `${accountCode} · ${accountName}`,
      changed: accountCode !== item.accountCode,
    },
    {
      label: "Centro de costo",
      before: item.costCenter ?? "sin centro de costo",
      after: costCenter || "sin centro de costo",
      changed: costCenter !== (item.costCenter ?? ""),
    },
    {
      label: "Tratamiento tributario",
      before: item.taxTreatment,
      after: taxTreatment,
      changed: taxTreatment !== item.taxTreatment,
    },
  ]
  const changed = changes.some((change) => change.changed)

  async function handleSubmit() {
    setTouched(true)
    if (reasonInvalid || !changed) return
    await onSubmit({ accountCode, costCenter, taxTreatment, reason: trimmed })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {t("Corregir clasificacion ·")}{" "}<span className="code">{item.serie}-{item.numero}</span>
          </DialogTitle>
          <DialogDescription>
            {item.supplierName} · {term.identificador_fiscal}{" "}
            <span className="code">{item.supplierRuc}</span> {" "}{t("· Total")}{" "}
            <span className="code font-medium text-foreground">{formatAmount(item.total)}</span>{" "}
            {item.currency}
          </DialogDescription>
        </DialogHeader>

        {/* Fundamento del motor: el punto de partida de la discusion. */}
        <div className="spine rounded-md border bg-surface-sunken p-2.5 [--spine-color:hsl(var(--muted-foreground))]">
          <div className="eyebrow mb-1">{t("Fundamento del motor")}</div>
          <p className="text-xs leading-[1.15rem] text-muted-foreground">{item.rationale}</p>
        </div>

        <fieldset className="grid gap-3 sm:grid-cols-2">
          <legend className="eyebrow mb-1.5">{t("Valores corregidos")}</legend>
          <div className="space-y-1">
            <Label htmlFor="cuenta">{t("Cuenta contable")}</Label>
            <Select value={accountCode} onValueChange={setAccountCode}>
              <SelectTrigger id="cuenta">
                <SelectValue placeholder={t("Selecciona una cuenta")} />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((account) => (
                  <SelectItem key={account.code} value={account.code}>
                    <span className="flex items-baseline gap-2">
                      <span className="code w-14 shrink-0 font-medium">{account.code}</span>
                      <span className="truncate text-muted-foreground">{account.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="cc">{t("Centro de costo")}</Label>
            <Select value={costCenter} onValueChange={setCostCenter}>
              <SelectTrigger id="cc">
                <SelectValue placeholder={t("Selecciona un centro de costo")} />
              </SelectTrigger>
              <SelectContent>
                {costCenters.map((cc) => (
                  <SelectItem key={cc.code} value={cc.code}>
                    <span className="code mr-1.5">{cc.code}</span>
                    {cc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* El tratamiento tributario NO viaja: `CorreccionIn` no lo acepta y el
              backend lo descartaria en silencio. El tratamiento sale de las
              reglas (statutory / tax / management), no de corregir un documento.
              Se deja visible porque forma parte de la decision que la persona
              esta tomando, y se dice al pie que no se guarda todavia.
              TODO(backend): `CorreccionIn.tratamiento`, o quitarlo de la SPA. */}
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="tratamiento">{t("Tratamiento tributario")}</Label>
            <Select value={taxTreatment} onValueChange={setTaxTreatment}>
              <SelectTrigger id="tratamiento">
                <SelectValue placeholder={t("Selecciona el tratamiento")} />
              </SelectTrigger>
              <SelectContent>
                {TAX_TREATMENT_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </fieldset>

        {/* Diff antes / despues */}
        <div className="overflow-hidden rounded-md border">
          <div className="grid grid-cols-[8.5rem_minmax(0,1fr)_1.25rem_minmax(0,1fr)] items-center gap-x-2 border-b bg-surface-sunken px-2.5 py-1">
            <span className="eyebrow">{t("Campo")}</span>
            <span className="eyebrow">{t("Propuesta del motor")}</span>
            <span />
            <span className="eyebrow">{t("Queda como")}</span>
          </div>
          {changes.map((change) => (
            <div
              key={change.label}
              className={cn(
                "grid grid-cols-[8.5rem_minmax(0,1fr)_1.25rem_minmax(0,1fr)] items-center gap-x-2 border-b px-2.5 py-1.5 text-xs last:border-b-0",
                change.changed ? "bg-brand-soft/50" : "",
              )}
            >
              <span className="truncate text-2xs uppercase tracking-[0.06em] text-muted-foreground">
                {change.label}
              </span>
              <span
                className={cn(
                  "truncate",
                  change.changed ? "text-muted-foreground line-through" : "text-muted-foreground",
                )}
                title={change.before}
              >
                {change.before}
              </span>
              <ArrowRight
                className={cn(
                  "h-3 w-3",
                  change.changed ? "text-brand" : "text-border-strong",
                )}
              />
              <span
                className={cn("truncate", change.changed && "font-semibold text-foreground")}
                title={change.after}
              >
                {change.after}
              </span>
            </div>
          ))}
        </div>

        <div className="space-y-1">
          <Label htmlFor="motivo-correccion">
            {t("Motivo de la correccion")}{" "}<span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="motivo-correccion"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            onBlur={() => setTouched(true)}
            placeholder={t("Por que la propuesta del motor no corresponde. Queda en el audit log.")}
            className="min-h-[4.5rem]"
            aria-invalid={touched && reasonInvalid}
          />
          <p
            className={cn(
              "text-2xs text-muted-foreground",
              touched && reasonInvalid && "font-medium text-destructive",
            )}
          >
            {t("Obligatorio. Minimo")}{" "}{MIN_REASON_LENGTH}{" "}{t("caracteres. Se guardan la cuenta y el centro de costo; el tratamiento tributario todavia no viaja al backend (sale de las reglas de tratamiento, no de esta correccion).")}</p>
        </div>

        <p className="flex items-start gap-1.5 rounded-md border border-brand/30 bg-brand-soft p-2 text-2xs leading-4">
          <Brain className="mt-px h-3.5 w-3.5 shrink-0 text-brand" />
          <span>
            {t("Esta correccion escribe la")}{" "}<strong>{t("memoria de proveedor")}</strong>: los proximos
            comprobantes de <span className="code">{item.supplierRuc}</span> {" "}{t("se clasificaran asi sin pasar por el modelo.")}</span>
        </p>

        <DialogFooter>
          {!changed ? (
            <p className="mr-auto self-center text-2xs text-muted-foreground">
              {t("Todavia no hay cambios respecto de la propuesta.")}</p>
          ) : null}
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={pending}>
            {t("Cancelar")}</Button>
          <Button size="sm" onClick={handleSubmit} disabled={pending || reasonInvalid || !changed}>
            {pending ? <Loader2 className="animate-spin" /> : null}
            {t("Guardar correccion")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
